import { type FC } from "react";

import { SectionIcon } from "./SectionIcon";
import { SessionButton } from "./SessionButton";
import { UnsupportedDeviceBanner } from "./UnsupportedDeviceBanner";
import { ImportSectionButton } from "./content/ImportSectionButton";

import { t } from "i18n/i18n";
import { Chip } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookSectionLinkedIcon, BookSectionTemplateIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourseCode,
  selectCurrentOrgName,
  selectCurrentSection,
  selectCurrentSectionHasMaterials,
  selectCurrentTermTitle,
  selectLinkedSectionCountForCurrentSection,
} from "store/selectors";
import { isLinkedSection, isTemplateSection } from "utils/publishing";
import { Link } from "utils/routing";
import { sectionHomeUrl } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

export const SectionTitleBar: FC = () => {
  const section = useAppSelector(selectCurrentSection);
  const isMobile = !useIsMdOrLarger();
  const { canAuthorCourse, canAuthorSection } = useCapabilities();
  const canViewTemplate = canAuthorCourse;
  const orgName = useAppSelector(selectCurrentOrgName);
  const courseTitle = useAppSelector(selectCurrentCourseCode);
  const termTitle = useAppSelector(selectCurrentTermTitle);
  const hasMaterials = useAppSelector(selectCurrentSectionHasMaterials);
  const linkedSectionCount = useAppSelector(selectLinkedSectionCountForCurrentSection);
  const showImportButton =
    !isMobile && canAuthorSection && !hasMaterials && !isLinkedSection(section);
  const ccmEnabled = isCCMEnabled();
  const isTemplate = isTemplateSection(section);
  const isLinked = isLinkedSection(section);

  if (!section) {
    return null;
  }

  return (
    <>
      <div className="flex w-full flex-wrap items-center justify-between gap-3 md:pt-4">
        <div className="flex flex-col justify-between gap-3">
          <div className="flex flex-row items-center gap-3">
            <SectionIcon section={section} size={isMobile ? "l" : "xl"} />

            <h2 className="flex items-center justify-between text-xl text-black-tint-20 md:text-2xl">
              {courseTitle && `${courseTitle}: `} {section.title}
            </h2>

            {showImportButton && <ImportSectionButton />}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              {orgName && termTitle && (
                <span>
                  {termTitle}, {orgName}
                </span>
              )}
              {section.description && (
                <Tooltip label={t("tooltip.view.section_description")} text={section.description} />
              )}
            </div>

            {ccmEnabled && isTemplate && (
              <>
                <Tooltip text={t("section_view.template_section_tooltip")}>
                  <Chip kind="hibiscusBorderless">
                    {t("home.add_section_modal.section_type_template_label")}
                  </Chip>
                </Tooltip>
                <IconText
                  className="text-xs font-medium text-black-tint-40"
                  iconStart={<BookSectionLinkedIcon />}
                  text={t("section_view.template_linked_sections", {
                    count: linkedSectionCount,
                  })}
                />
              </>
            )}

            {ccmEnabled && isLinked && (
              <>
                <Tooltip text={t("section_view.linked_section_tooltip")}>
                  <Chip kind="mossBorderless">
                    {t("home.add_section_modal.section_type_linked_label")}
                  </Chip>
                </Tooltip>
                {canViewTemplate && section.template_id && (
                  <Link to={sectionHomeUrl(section.template_id)}>
                    <IconText
                      className="body-xs gap-1.5 text-blue-shade-20"
                      iconStart={<BookSectionTemplateIcon />}
                      text={t("section_view.view_template")}
                    />
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
        {!isTemplate && <SessionButton />}
      </div>
      <UnsupportedDeviceBanner />
    </>
  );
};
